import { useCallback, useEffect, useRef, useState } from "react";
import type * as PdfjsTypes from "pdfjs-dist";
import type { Mengen, MengenWall } from "@bidi-handel/core/schemas";
import { polygonMetricsPt } from "./plan-geometry";

type Feature = Mengen["plaene"][number]["polygons"]["features"][number];
type EditorMode = "view" | "scale" | "draw" | "wall";

/**
 * Editor-Host: rendert die Plan-PDF-Seite client-seitig via pdfjs auf ein
 * <canvas> (überabgetastet für Schärfe) und legt ein SVG-Polygon-Overlay
 * darüber. Stufenloses Zoom (Wheel, Zoom-to-Cursor) + Pan (Leertaste/Mittelmaus-
 * Drag) per CSS-Transform; Lupe beim Vertex-Drag. Edit-Modi:
 *  - "view": Raum wählen, Vertices draggen.
 *  - "scale": zwei Punkte klicken → Parent fragt die reale Distanz ab.
 *
 * Koordinaten: Engine-Polygone sind in PDF-Pt (y-up). pdfjs' PageViewport.
 * convertTo{Viewport,Pdf}Point übernimmt das Mapping ↔ Render-Pixel. Maus→SVG
 * läuft über das Bounding-Rect-Verhältnis und ist damit zoom-/pan-robust.
 */

let pdfjsModule: typeof PdfjsTypes | null = null;
let pdfjsInit: Promise<typeof PdfjsTypes> | null = null;
async function loadPdfjs(): Promise<typeof PdfjsTypes> {
  if (pdfjsModule) return pdfjsModule;
  if (!pdfjsInit) {
    pdfjsInit = (async () => {
      const mod = await import("pdfjs-dist");
      const workerUrl = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url"))
        .default;
      mod.GlobalWorkerOptions.workerSrc = workerUrl;
      pdfjsModule = mod;
      return mod;
    })();
  }
  return pdfjsInit;
}

/** Überabtastung: Canvas wird in OVERSAMPLE×Anzeigegröße gerendert → scharf bis ~OVERSAMPLE×-Zoom. */
const OVERSAMPLE = 2;
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 12;
const MAG_SIZE = 190; // Lupen-Panel px
const MAG_ZOOM = 4; // Lupen-Vergrößerung relativ zur Anzeige
/**
 * Schärfe bei Zoom: Beim Hineinzoomen wird die Seite mit höherer Backing-Dichte NEU gerendert
 * (pdfjs ist Vektor → scharf), statt das fixe Bitmap per CSS hochzuskalieren. Das Backing wächst
 * auf coordScale×density; das Koordinatensystem (viewport/viewBox/cssSize) bleibt unverändert.
 * Voll scharf bis ~MAX_DENSITY×; darüber (bis MAX_ZOOM) wird wieder hochskaliert — die Lupe
 * sampelt aber weiter das dichte Backing. Höheres MAX_DENSITY = schärfer bei extremem Zoom,
 * aber quadratisch mehr Speicher (Canvas-Bytes = w·h·4). MAX_CANVAS_EDGE deckelt die Kantenlänge
 * (Browser liefern darüber ein leeres Canvas OHNE Exception). 16384 ist auf Desktop-Chromium/
 * Firefox sicher; iOS-Safari ist strenger (~4096–8192) — kein Zielgerät für den Innendienst-Editor.
 */
const MAX_DENSITY = 4;
const MAX_CANVAS_EDGE = 16384;

/** Ein optionaler PDF-Layer (Optional Content Group, #11). */
export interface OcgLayer {
  id: string;
  name: string;
  visible: boolean;
}

// pdfjs exportiert den OptionalContentConfig-Typ nicht namentlich — über den
// Rückgabetyp von getOptionalContentConfig ableiten (version-robust).
type OcgConfig = Awaited<
  ReturnType<PdfjsTypes.PDFDocumentProxy["getOptionalContentConfig"]>
>;

export interface PlanCanvasEditorProps {
  pdfUrl: string;
  pageIndex: number;
  ratio: number;
  features: Feature[];
  selectedRoomId: string | null;
  /** Räume einer fokussierten Gruppe (z.B. Wohneinheit) — werden im Plan
   *  hervorgehoben, ohne den Einzel-Pick (`selectedRoomId`) zu ersetzen. */
  highlightRoomIds?: Set<string>;
  /** #11: meldet die OCG-Layer der PDF nach dem Laden (leer, wenn keine). */
  onOcgLayers?: (layers: OcgLayer[]) => void;
  /** #11: gewünschte Layer-Sichtbarkeit (id→visible). Änderung rastert neu. */
  ocgVisibility?: Record<string, boolean>;
  mode: EditorMode;
  /** Wände des aktuell gewählten Raums (P2c) — werden gezeichnet + editiert. */
  selectedRoomWalls: MengenWall[];
  onSelectRoom: (id: string | null) => void;
  onCommitPolygon: (polygonId: string, coordinates: number[][][]) => void;
  onScalePicked: (
    p1: { x: number; y: number },
    p2: { x: number; y: number }
  ) => void;
  /** Neues Raum-Polygon gezeichnet (P2b) — Koordinaten in PDF-Pt. */
  onCreatePolygon: (coordinates: number[][][]) => void;
  /** Wand-Striche des gewählten Raums geändert (P2c). */
  onCommitWalls: (walls: MengenWall[]) => void;
}

interface View {
  scale: number; // CSS-Zoom über die Anzeigegröße
  tx: number; // Pan in CSS-Px
  ty: number;
}

interface DragState {
  polygonId: string;
  vertexIndex: number;
  ringPx: Array<[number, number]>; // Render-Px (SVG-viewBox-Koords)
  moved: boolean; // true, sobald der Vertex tatsächlich gezogen wurde (kein No-Op-Commit)
}

export function PlanCanvasEditor(props: PlanCanvasEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const magCanvasRef = useRef<HTMLCanvasElement>(null);

  // Schärfe-Re-Render: page/doc/coordScale + Render-Task in Refs halten, damit der
  // Density-Effekt die Seite neu rastern kann, ohne das Dokument neu zu laden.
  const pageRef = useRef<PdfjsTypes.PDFPageProxy | null>(null);
  const docRef = useRef<PdfjsTypes.PDFDocumentProxy | null>(null);
  // #11: Optional-Content-Config (OCG-Layer) der PDF, falls vorhanden.
  const ocgConfigRef = useRef<OcgConfig | null>(null);
  // Gewünschte Layer-Sichtbarkeit als Ref, damit renderPageBitmap (deps []) den
  // aktuellen Stand liest, ohne neu erzeugt zu werden.
  const ocgVisibilityRef = useRef<Record<string, boolean> | undefined>(undefined);
  ocgVisibilityRef.current = props.ocgVisibility;
  const coordScaleRef = useRef<number>(0); // fit·OVERSAMPLE = Basis des Koordinatensystems
  const renderTaskRef = useRef<PdfjsTypes.RenderTask | null>(null);
  const densityRef = useRef<number>(1); // aktuell gerasterte Backing-Dichte
  const densityCapRef = useRef<number>(MAX_DENSITY); // aus Canvas-Kantenlimit abgeleitet
  const pendingDensityRef = useRef<number | null>(null); // während Geste aufgeschobenes Level
  const genRef = useRef<number>(0); // Load-Generation gegen stale page nach Plan-Wechsel/StrictMode

  const [viewport, setViewport] = useState<PdfjsTypes.PageViewport | null>(null);
  const [bitmapVersion, setBitmapVersion] = useState(0); // hochgezählt nach jedem Re-Render → Lupe neu zeichnen
  const [cssSize, setCssSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [view, setView] = useState<View>({ scale: 1, tx: 0, ty: 0 });
  const [drag, setDrag] = useState<DragState | null>(null);
  const [scalePts, setScalePts] = useState<Array<[number, number]>>([]);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const panRef = useRef<{ startX: number; startY: number; tx: number; ty: number } | null>(null);
  // true, sobald eine Pan-Geste sich bewegt hat — entkoppelt die Klick-Unterdrückung
  // von spaceHeld (das beim keyup vor dem pointerup wegfallen kann).
  const didPanRef = useRef(false);
  const [magnifier, setMagnifier] = useState<{ x: number; y: number } | null>(null);
  // P2b: Punkte eines neu gezeichneten Raum-Polygons (SVG-viewBox-Koords).
  const [drawPts, setDrawPts] = useState<Array<[number, number]>>([]);
  // P2c: erster Klick eines neuen Wand-Strichs (SVG-Koords) bzw. Endpunkt-Drag.
  const [wallStart, setWallStart] = useState<[number, number] | null>(null);
  const [wallDrag, setWallDrag] = useState<{
    wallId: string;
    end: "start" | "end";
    px: [number, number];
  } | null>(null);

  // ---- Seite mit gegebener Backing-Dichte rastern (Schärfe-Re-Render) ----
  // Setzt NUR canvas.width/height + zeichnet. Das Koordinatensystem (viewport-State, viewBox,
  // cssSize) bleibt UNBERÜHRT — niemals setViewport(vpRender) aufrufen, das zerstört das Mapping.
  // gen schützt gegen stale page nach Plan-Wechsel/StrictMode (doc.destroy()).
  const renderPageBitmap = useCallback(async (density: number, gen: number) => {
    const page = pageRef.current;
    const canvas = canvasRef.current;
    const coordScale = coordScaleRef.current;
    if (!page || !canvas || !coordScale || gen !== genRef.current) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Laufenden Render auf demselben Canvas erst abbrechen + abwarten (pdfjs verbietet zwei
    // gleichzeitige render() auf einem Canvas).
    const prev = renderTaskRef.current;
    if (prev) {
      prev.cancel();
      try {
        await prev.promise;
      } catch {
        /* RenderingCancelledException — erwartet */
      }
    }
    if (gen !== genRef.current) return;
    const vpRender = page.getViewport({ scale: coordScale * density });
    canvas.width = Math.round(vpRender.width);
    canvas.height = Math.round(vpRender.height);
    // #11: gewünschte OCG-Layer-Sichtbarkeit auf die Config anwenden + an den
    // Render durchreichen (pdfjs respektiert sie beim Rastern).
    const ocg = ocgConfigRef.current;
    let optionalContentConfigPromise: Promise<OcgConfig> | undefined;
    if (ocg) {
      const vis = ocgVisibilityRef.current;
      if (vis) for (const [id, v] of Object.entries(vis)) ocg.setVisibility(id, v);
      optionalContentConfigPromise = Promise.resolve(ocg);
    }
    let task: PdfjsTypes.RenderTask;
    try {
      task = page.render({
        canvas,
        canvasContext: ctx,
        viewport: vpRender,
        optionalContentConfigPromise,
      });
    } catch {
      return; // page evtl. zerstört (Plan-Wechsel) → ignorieren
    }
    renderTaskRef.current = task;
    try {
      await task.promise;
    } catch {
      return; // Cancel oder Plan-Wechsel — UI nicht brechen
    }
    if (gen !== genRef.current) return;
    if (renderTaskRef.current === task) renderTaskRef.current = null;
    densityRef.current = density;
    setBitmapVersion((v) => v + 1);
  }, []);

  // ---- PDF laden + Seite rendern (überabgetastet, Breite des Containers füllen) ----
  useEffect(() => {
    let cancelled = false;
    const gen = genRef.current + 1;
    genRef.current = gen;
    setLoading(true);
    setError(null);
    loadPdfjs()
      .then((mod) => mod.getDocument(props.pdfUrl).promise)
      .then(async (d) => {
        if (cancelled || gen !== genRef.current) {
          d.destroy();
          return;
        }
        docRef.current = d;
        // #11: OCG-Layer (Optional Content Groups) lesen + an den Parent melden.
        try {
          const ocg = await d.getOptionalContentConfig();
          ocgConfigRef.current = ocg;
          // getOrder() liefert die Anzeige-Reihenfolge: Group-IDs (string) +
          // ggf. verschachtelte { name, order }-Objekte → flach sammeln.
          const ids: string[] = [];
          const collect = (arr: unknown): void => {
            if (!Array.isArray(arr)) return;
            for (const it of arr) {
              if (typeof it === "string") ids.push(it);
              else if (it && typeof it === "object" && "order" in it)
                collect((it as { order: unknown }).order);
            }
          };
          collect(ocg.getOrder());
          const layers: OcgLayer[] = ids.map((id) => {
            const g = ocg.getGroup(id) as { name?: string | null } | null;
            return { id, name: g?.name ?? id, visible: ocg.isVisible(id) };
          });
          if (!cancelled && gen === genRef.current) props.onOcgLayers?.(layers);
        } catch {
          ocgConfigRef.current = null;
          if (!cancelled && gen === genRef.current) props.onOcgLayers?.([]);
        }
        const page = await d.getPage(props.pageIndex + 1);
        if (cancelled || gen !== genRef.current) return;
        pageRef.current = page;
        const base = page.getViewport({ scale: 1 });
        const containerW = containerRef.current?.clientWidth || 900;
        const fit = Math.max(0.1, containerW / base.width);
        const coordScale = fit * OVERSAMPLE;
        coordScaleRef.current = coordScale;
        const coordVp = page.getViewport({ scale: coordScale });
        // Backing-Kantenlimit → maximal mögliche Dichte (sonst leeres Canvas ohne Exception).
        densityCapRef.current = Math.max(
          1,
          Math.min(MAX_DENSITY, Math.floor(MAX_CANVAS_EDGE / Math.max(coordVp.width, coordVp.height)))
        );
        densityRef.current = 1;
        // Initial bei Dichte 1 rastern (view.scale startet bei 1). renderPageBitmap setzt
        // canvas.width/height; der viewport-STATE bleibt der coordScale-Viewport.
        await renderPageBitmap(1, gen);
        if (cancelled || gen !== genRef.current) return;
        setViewport(coordVp);
        setCssSize({ w: coordVp.width / OVERSAMPLE, h: coordVp.height / OVERSAMPLE });
        setView({ scale: 1, tx: 0, ty: 0 });
        setLoading(false);
      })
      .catch((err: unknown) => {
        // Abgebrochene Renders (RenderingCancelledException) sind kein Fehler.
        if (cancelled || (err as { name?: string })?.name === "RenderingCancelledException")
          return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
      const d = docRef.current;
      pageRef.current = null;
      docRef.current = null;
      pendingDensityRef.current = null;
      if (d) d.destroy();
    };
  }, [props.pdfUrl, props.pageIndex, renderPageBitmap]);

  // #11: Layer-Sichtbarkeit geändert → Seite bei aktueller Dichte neu rastern.
  useEffect(() => {
    if (loading || !pageRef.current || !ocgConfigRef.current) return;
    void renderPageBitmap(densityRef.current, genRef.current);
  }, [props.ocgVisibility, loading, renderPageBitmap]);

  // ---- Schärfe: bei Zoom-Stufenwechsel die Seite mit höherer Backing-Dichte neu rastern ----
  useEffect(() => {
    if (loading || !viewport) return;
    const cap = Math.min(MAX_DENSITY, densityCapRef.current);
    const target = stairDensity(view.scale, densityRef.current, cap);
    if (target === densityRef.current) return;
    // Während einer laufenden Geste NICHT neu rastern (canvas.width=… leert das Canvas → Weißblitz
    // genau im Präzisions-Moment). Level merken und nach Gesten-Ende nachholen.
    const gestureActive =
      !!drag ||
      !!wallDrag ||
      !!panRef.current ||
      drawPts.length > 0 ||
      !!wallStart ||
      scalePts.length > 0;
    if (gestureActive) {
      pendingDensityRef.current = target;
      return;
    }
    pendingDensityRef.current = null;
    const gen = genRef.current;
    // Debounce: schnelles Durchscrollen mehrerer Stufen feuert nur den letzten Render.
    const t = window.setTimeout(() => renderPageBitmap(target, gen), 150);
    return () => window.clearTimeout(t);
  }, [view.scale, loading, viewport, drag, wallDrag, drawPts, wallStart, scalePts, renderPageBitmap]);

  // ---- Leertaste = Pan-Modus ----
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code === "Space" && !e.repeat) setSpaceHeld(true);
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === "Space") setSpaceHeld(false);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  // ---- Wheel-Zoom-to-Cursor (native Listener für preventDefault) ----
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      setView((v) => {
        const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
        const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v.scale * factor));
        if (next === v.scale) return v;
        // Layout-Punkt unter dem Cursor fixhalten.
        const lx = (cx - v.tx) / v.scale;
        const ly = (cy - v.ty) / v.scale;
        return { scale: next, tx: cx - lx * next, ty: cy - ly * next };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // ---- globales pointerup beendet Vertex-Drag / Pan ----
  useEffect(() => {
    if (!drag || !viewport) return;
    const onUp = () => {
      // Nur committen, wenn der Vertex tatsächlich bewegt wurde — ein bloßes
      // Antippen soll keinen Server-PATCH (+ dirty/Recompute) auslösen.
      if (drag.moved) {
        const ringPt = drag.ringPx.map((p) => {
          const [x, y] = viewport.convertToPdfPoint(p[0], p[1]) as [number, number];
          return [x, y];
        });
        if (ringPt.length > 0) ringPt.push([ringPt[0]![0]!, ringPt[0]![1]!]);
        props.onCommitPolygon(drag.polygonId, [ringPt]);
      }
      setDrag(null);
      setMagnifier(null);
    };
    window.addEventListener("pointerup", onUp);
    return () => window.removeEventListener("pointerup", onUp);
  }, [drag, viewport, props]);

  // ---- globales pointerup beendet Wand-Endpunkt-Drag (P2c) ----
  useEffect(() => {
    if (!wallDrag || !viewport) return;
    const onUp = () => {
      const [x, y] = viewport.convertToPdfPoint(
        wallDrag.px[0],
        wallDrag.px[1]
      ) as [number, number];
      const walls = props.selectedRoomWalls.map((w) =>
        w.id === wallDrag.wallId ? { ...w, [wallDrag.end]: { x, y } } : w
      );
      props.onCommitWalls(walls);
      setWallDrag(null);
      setMagnifier(null);
      window.setTimeout(() => (didWallDragRef.current = false), 0);
    };
    window.addEventListener("pointerup", onUp);
    return () => window.removeEventListener("pointerup", onUp);
  }, [wallDrag, viewport, props]);

  // Pan endet bei jedem pointerup (Ref-Set löst kein Re-Render aus → einmalig registrieren).
  useEffect(() => {
    const onUp = () => {
      panRef.current = null;
      // didPan erst NACH dem synthetischen click zurücksetzen, den der Browser
      // nach pointerup feuert — sonst greift die Klick-Unterdrückung nicht.
      window.setTimeout(() => {
        didPanRef.current = false;
      }, 0);
      // Während des Pans aufgeschobenes Density-Level nachholen (der Density-Effekt
      // re-triggert bei Pan-Ende nicht, weil panRef ein Ref ist).
      const target = pendingDensityRef.current;
      if (target !== null && target !== densityRef.current) {
        pendingDensityRef.current = null;
        renderPageBitmap(target, genRef.current);
      }
    };
    window.addEventListener("pointerup", onUp);
    return () => window.removeEventListener("pointerup", onUp);
  }, [renderPageBitmap]);

  // ---- Maus → SVG-viewBox-Koords (zoom-/pan-robust via Rect-Verhältnis) ----
  const svgPoint = useCallback(
    (clientX: number, clientY: number): [number, number] => {
      const rect = svgRef.current?.getBoundingClientRect();
      const vp = viewport;
      if (!rect || !vp || rect.width === 0) return [0, 0];
      return [
        (clientX - rect.left) * (vp.width / rect.width),
        (clientY - rect.top) * (vp.height / rect.height),
      ];
    },
    [viewport]
  );

  // SVG-viewBox-Koords → PDF-Pt.
  const toPdf = useCallback(
    (px: [number, number]): [number, number] =>
      viewport
        ? (viewport.convertToPdfPoint(px[0], px[1]) as [number, number])
        : [0, 0],
    [viewport]
  );

  // Ring (Render-Px) → PDF-Pt schließen + committen (P2a Vertex add/del).
  const commitRingPx = useCallback(
    (polygonId: string, ringPx: Array<[number, number]>) => {
      if (!viewport) return;
      const ringPt = ringPx.map(toPdf) as number[][];
      if (ringPt.length > 0) ringPt.push([ringPt[0]![0]!, ringPt[0]![1]!]);
      props.onCommitPolygon(polygonId, [ringPt]);
    },
    [viewport, toPdf, props]
  );

  // true, sobald ein Wand-Endpunkt angefasst wurde — unterdrückt den danach
  // gefeuerten svg-Klick (sonst entstünde ein ungewollter neuer Wand-Punkt).
  const didWallDragRef = useRef(false);

  function uniqueVerticesPx(feature: Feature): Array<[number, number]> {
    const ring = feature.geometry.coordinates[0] ?? [];
    const open = dropClosingDup(ring);
    return open.map((pt) => {
      const [x, y] = viewport!.convertToViewportPoint(pt[0]!, pt[1]!) as [number, number];
      return [x, y];
    });
  }

  function onVertexDown(
    e: React.PointerEvent,
    polygonId: string,
    vertexIndex: number,
    ringPx: Array<[number, number]>
  ) {
    if (props.mode !== "view" || spaceHeld) return;
    // Nur Linksklick zieht einen Vertex; Mittel-/Rechtsklick durchpropagieren
    // lassen (sonst stirbt der Mittelmaus-Pan über einem Vertex).
    if (e.button !== 0) return;
    // P2a: Alt/Ctrl/Meta-Klick löscht den Vertex (mind. 3 müssen bleiben).
    if (e.altKey || e.ctrlKey || e.metaKey) {
      e.stopPropagation();
      if (ringPx.length <= 3) return;
      commitRingPx(polygonId, ringPx.filter((_, i) => i !== vertexIndex));
      return;
    }
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({
      polygonId,
      vertexIndex,
      ringPx: ringPx.map((p) => [...p] as [number, number]),
      moved: false,
    });
    setMagnifier({ x: ringPx[vertexIndex]![0], y: ringPx[vertexIndex]![1] });
  }

  function onContentPointerDown(e: React.PointerEvent) {
    // Pan starten bei Leertaste oder Mittelmaus.
    if (spaceHeld || e.button === 1) {
      e.preventDefault();
      didPanRef.current = false;
      e.currentTarget.setPointerCapture(e.pointerId);
      panRef.current = { startX: e.clientX, startY: e.clientY, tx: view.tx, ty: view.ty };
    }
  }

  function onContentPointerMove(e: React.PointerEvent) {
    if (panRef.current) {
      didPanRef.current = true;
      const p = panRef.current;
      setView((v) => ({ ...v, tx: p.tx + (e.clientX - p.startX), ty: p.ty + (e.clientY - p.startY) }));
      return;
    }
    if (wallDrag) {
      const pt = svgPoint(e.clientX, e.clientY);
      setWallDrag((d) => (d ? { ...d, px: pt } : d));
      setMagnifier({ x: pt[0], y: pt[1] });
      return;
    }
    if (drag) {
      const pt = svgPoint(e.clientX, e.clientY);
      setDrag((d) =>
        d ? { ...d, moved: true, ringPx: d.ringPx.map((v, i) => (i === d.vertexIndex ? pt : v)) } : d
      );
      setMagnifier({ x: pt[0], y: pt[1] });
      return;
    }
    // Lupe folgt dem Cursor auch beim Platzieren NEUER Punkte (Raum P2b / Wand P2c / Maßstab),
    // nicht nur beim Ziehen bestehender Vertices.
    if (props.mode === "draw" || props.mode === "wall" || props.mode === "scale") {
      const pt = svgPoint(e.clientX, e.clientY);
      setMagnifier({ x: pt[0], y: pt[1] });
    }
  }

  // P2c: Wand-Endpunkt anfassen → Drag (verlängern/verkürzen) bzw. Alt-Klick = löschen.
  function onWallEndpointDown(
    e: React.PointerEvent,
    wallId: string,
    end: "start" | "end",
    px: [number, number]
  ) {
    if (props.mode !== "wall" || spaceHeld || e.button !== 0) return;
    e.stopPropagation();
    didWallDragRef.current = true;
    if (e.altKey || e.ctrlKey || e.metaKey) {
      props.onCommitWalls(props.selectedRoomWalls.filter((w) => w.id !== wallId));
      window.setTimeout(() => (didWallDragRef.current = false), 0);
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    setWallDrag({ wallId, end, px });
    setMagnifier({ x: px[0], y: px[1] });
  }

  function onSvgClick(e: React.MouseEvent) {
    // didPanRef/didWallDragRef: ein gerade beendeter Pan bzw. Wand-Endpunkt-Drag
    // darf keinen neuen Punkt setzen.
    if (spaceHeld || didPanRef.current || didWallDragRef.current) return;
    const p = svgPoint(e.clientX, e.clientY);

    if (props.mode === "scale") {
      setScalePts((prev) => {
        const next = [...prev, p];
        if (next.length === 2 && viewport) {
          const a = viewport.convertToPdfPoint(next[0]![0], next[0]![1]) as [number, number];
          const b = viewport.convertToPdfPoint(next[1]![0], next[1]![1]) as [number, number];
          props.onScalePicked({ x: a[0], y: a[1] }, { x: b[0], y: b[1] });
          return [];
        }
        return next;
      });
      return;
    }

    if (props.mode === "draw") {
      // Klick nahe dem ersten Punkt (≥3 Punkte) schließt das Polygon.
      if (drawPts.length >= 3) {
        const f = drawPts[0]!;
        const thr = (14 * OVERSAMPLE) / view.scale;
        if (Math.hypot(p[0] - f[0], p[1] - f[1]) <= thr) {
          const ringPt = drawPts.map(toPdf) as number[][];
          ringPt.push([ringPt[0]![0]!, ringPt[0]![1]!]);
          props.onCreatePolygon([ringPt]);
          setDrawPts([]);
          return;
        }
      }
      setDrawPts((prev) => [...prev, p]);
      return;
    }

    if (props.mode === "wall") {
      if (!props.selectedRoomId) return; // Wand braucht einen gewählten Raum
      if (wallStart === null) {
        setWallStart(p);
      } else {
        const a = toPdf(wallStart);
        const b = toPdf(p);
        const wall: MengenWall = {
          id: newWallId(),
          start: { x: a[0], y: a[1] },
          end: { x: b[0], y: b[1] },
        };
        props.onCommitWalls([...props.selectedRoomWalls, wall]);
        setWallStart(null);
      }
      return;
    }
  }

  // Mode-Wechsel / Escape: laufende Zeichen-Gesten verwerfen.
  useEffect(() => {
    setScalePts([]);
    setDrawPts([]);
    setWallStart(null);
    setMagnifier(null); // Hover-Lupe aus dem alten Modus nicht stehen lassen
  }, [props.mode]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setDrawPts([]);
        setWallStart(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ---- Lupe zeichnen ----
  useEffect(() => {
    const mag = magCanvasRef.current;
    const main = canvasRef.current;
    if (!mag || !main || !magnifier) return;
    const ctx = mag.getContext("2d");
    if (!ctx) return;
    // magnifier.x/y sind in SVG-viewBox-Einheiten. Durch den Schärfe-Re-Render ist das Canvas-
    // Backing d× größer als die viewBox (d = canvas.width/viewport.width, live gelesen — stimmt
    // auch unmittelbar nach einem Density-Swap). Zentrum UND Ausschnitt mit d skalieren; sonst
    // sitzt das Fadenkreuz nicht auf dem Punkt. /view.scale: der Ausschnitt zeigt MAG_ZOOM× über
    // die aktuelle Bildschirm-Ansicht hinaus.
    const d = viewport && viewport.width > 0 ? main.width / viewport.width : 1;
    const srcSize = ((MAG_SIZE / MAG_ZOOM) * OVERSAMPLE / view.scale) * d;
    const sx = magnifier.x * d - srcSize / 2;
    const sy = magnifier.y * d - srcSize / 2;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, MAG_SIZE, MAG_SIZE);
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, MAG_SIZE, MAG_SIZE);
    ctx.drawImage(main, sx, sy, srcSize, srcSize, 0, 0, MAG_SIZE, MAG_SIZE);
    // Fadenkreuz
    ctx.strokeStyle = "#5b9cff";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(MAG_SIZE / 2, 0);
    ctx.lineTo(MAG_SIZE / 2, MAG_SIZE);
    ctx.moveTo(0, MAG_SIZE / 2);
    ctx.lineTo(MAG_SIZE, MAG_SIZE / 2);
    ctx.stroke();
  }, [magnifier, view.scale, viewport, bitmapVersion]);

  function zoomBy(factor: number) {
    const el = containerRef.current;
    if (!el) return;
    const cx = el.clientWidth / 2;
    const cy = el.clientHeight / 2;
    setView((v) => {
      const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v.scale * factor));
      const lx = (cx - v.tx) / v.scale;
      const ly = (cy - v.ty) / v.scale;
      return { scale: next, tx: cx - lx * next, ty: cy - ly * next };
    });
  }

  if (error) {
    return (
      <div
        className="warn-line"
        style={{ borderLeftColor: "var(--bad)", background: "oklch(0.72 0.18 25 / 0.08)" }}
      >
        <strong>Plan nicht ladbar:</strong> <span className="mono">{error}</span>
      </div>
    );
  }

  const panCursor = spaceHeld || panRef.current ? "grab" : props.mode === "scale" ? "crosshair" : "default";
  // SVG-User-Unit → Screen-Px-Faktor = (1/OVERSAMPLE)·view.scale. Damit Strich,
  // Vertex-Radius und Label bildschirm-konstant bleiben, mit OVERSAMPLE/view.scale skalieren.
  const u = OVERSAMPLE / view.scale;

  return (
    <div style={{ position: "relative" }}>
      {/* Zoom-Toolbar */}
      <div
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          marginBottom: 6,
          fontSize: 12,
        }}
      >
        <button type="button" className="btn ghost" onClick={() => zoomBy(1 / 1.3)} aria-label="Auszoomen">
          −
        </button>
        <span className="mono dim" style={{ minWidth: 48, textAlign: "center" }}>
          {Math.round(view.scale * 100)}%
        </span>
        <button type="button" className="btn ghost" onClick={() => zoomBy(1.3)} aria-label="Einzoomen">
          +
        </button>
        <button type="button" className="btn ghost" onClick={() => setView({ scale: 1, tx: 0, ty: 0 })}>
          Anpassen
        </button>
        <span className="dim" style={{ fontSize: 11 }}>
          Mausrad = Zoom · Leertaste/Mittelmaus + ziehen = verschieben
        </span>
      </div>

      <div
        ref={containerRef}
        style={{
          position: "relative",
          overflow: "hidden",
          height: "76vh",
          background: "var(--bg-void)",
          borderRadius: 8,
          cursor: panCursor,
          display: loading ? "grid" : "block",
          placeItems: "center",
        }}
        onPointerDown={onContentPointerDown}
        onPointerMove={onContentPointerMove}
        onPointerLeave={() => {
          // Hover-Lupe entfernen, wenn die Maus den Plan verlässt — aber nicht mitten in einem
          // Drag (Pointer-Capture hält den Vertex/die Wand, die Lupe soll bleiben).
          if (!drag && !wallDrag) setMagnifier(null);
        }}
      >
        {loading && (
          <div className="dim" style={{ padding: 40 }}>
            <span
              className="spinner"
              style={{ borderColor: "var(--acc)", borderRightColor: "transparent" }}
            />{" "}
            Plan wird gerendert …
          </div>
        )}
        {/* Wrapper + Canvas IMMER gemountet: das Render im Lade-Effekt braucht
            canvasRef.current. Ein {viewport && …}-Gate würde das Canvas erst nach
            dem Render mounten → Henne-Ei-Deadlock. Sichtbar erst nach dem Load. */}
        <div
          ref={contentRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: cssSize.w || 1,
            height: cssSize.h || 1,
            transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`,
            transformOrigin: "0 0",
            display: viewport && !loading ? "block" : "none",
          }}
        >
          <canvas
            ref={canvasRef}
            style={{ display: "block", width: cssSize.w, height: cssSize.h, background: "white" }}
          />
          {viewport && (
            <svg
              ref={svgRef}
              width={cssSize.w}
              height={cssSize.h}
              viewBox={`0 0 ${viewport.width} ${viewport.height}`}
              style={{ position: "absolute", top: 0, left: 0 }}
              onClick={onSvgClick}
            >
              {props.features.map((f) => {
                const id = f.properties.id;
                const isSel = id === props.selectedRoomId;
                const isHi = !isSel && (props.highlightRoomIds?.has(id) ?? false);
                const ringPx =
                  drag && drag.polygonId === id ? drag.ringPx : uniqueVerticesPx(f);
                const pointsAttr = ringPx.map((p) => `${p[0]},${p[1]}`).join(" ");
                const liveRing = ringPx.map((p) => {
                  const [x, y] = viewport.convertToPdfPoint(p[0], p[1]) as [number, number];
                  return [x, y];
                });
                const m = polygonMetricsPt(liveRing, props.ratio);
                let cx = 0;
                let cy = 0;
                for (const p of ringPx) {
                  cx += p[0];
                  cy += p[1];
                }
                const centroid: [number, number] = ringPx.length
                  ? [cx / ringPx.length, cy / ringPx.length]
                  : [0, 0];
                // Bildschirm-konstante Strichbreite/Radius trotz Zoom (u-Faktor).
                const sw = (isSel || isHi ? 2 : 1) * u;
                const vr = 5 * u;
                return (
                  <g key={id}>
                    <polygon
                      points={pointsAttr}
                      onClick={(e) => {
                        // didPanRef: ein gerade beendeter Pan darf keinen Raum (de)selektieren.
                        if (props.mode === "view" && !spaceHeld && !didPanRef.current) {
                          e.stopPropagation();
                          props.onSelectRoom(isSel ? null : id);
                        }
                      }}
                      style={{
                        fill: isSel
                          ? "oklch(0.72 0.15 250 / 0.25)"
                          : isHi
                            ? "oklch(0.78 0.16 152 / 0.16)"
                            : "oklch(0.72 0.15 250 / 0.08)",
                        stroke: isSel
                          ? "var(--acc)"
                          : isHi
                            ? "oklch(0.78 0.16 152 / 0.7)"
                            : "oklch(0.72 0.15 250 / 0.5)",
                        strokeWidth: sw,
                        cursor: props.mode === "view" ? "pointer" : "inherit",
                      }}
                    />
                    {isSel && (
                      <text
                        x={centroid[0]}
                        y={centroid[1]}
                        textAnchor="middle"
                        style={{
                          fill: "var(--acc)",
                          fontSize: 12 * u,
                          fontWeight: 600,
                          pointerEvents: "none",
                        }}
                      >
                        {m.areaM2.toFixed(1)} m²
                      </text>
                    )}
                    {isSel &&
                      props.mode === "view" &&
                      ringPx.map((p, i) => (
                        <circle
                          key={i}
                          cx={p[0]}
                          cy={p[1]}
                          r={vr}
                          style={{ fill: "var(--acc)", cursor: "grab", stroke: "white", strokeWidth: sw }}
                          onPointerDown={(e) => onVertexDown(e, id, i, ringPx)}
                        />
                      ))}
                    {/* P2a: „+"-Handles auf den Kanten-Mittelpunkten → Vertex einfügen.
                        Alt-Klick auf einen Vertex löscht ihn. */}
                    {isSel &&
                      props.mode === "view" &&
                      ringPx.map((p, i) => {
                        const nxt = ringPx[(i + 1) % ringPx.length]!;
                        const mx = (p[0] + nxt[0]) / 2;
                        const my = (p[1] + nxt[1]) / 2;
                        return (
                          <circle
                            key={`mid-${i}`}
                            cx={mx}
                            cy={my}
                            r={vr * 0.62}
                            style={{
                              fill: "white",
                              stroke: "var(--acc)",
                              strokeWidth: sw,
                              cursor: "copy",
                              opacity: 0.85,
                            }}
                            onPointerDown={(e) => {
                              if (e.button !== 0 || spaceHeld) return;
                              e.stopPropagation();
                              const next = [...ringPx];
                              next.splice(i + 1, 0, [mx, my]);
                              commitRingPx(id, next);
                            }}
                          />
                        );
                      })}
                  </g>
                );
              })}
              {props.mode === "scale" &&
                scalePts.map((p, i) => (
                  <circle key={i} cx={p[0]} cy={p[1]} r={5 * u} style={{ fill: "orange" }} />
                ))}

              {/* P2c: Wand-Striche des gewählten Raums (orange). In „wall"-Modus
                  Endpunkte draggen (verlängern/verkürzen), Alt-Klick = löschen. */}
              {props.selectedRoomId &&
                props.selectedRoomWalls.map((w) => {
                  const aPx =
                    wallDrag && wallDrag.wallId === w.id && wallDrag.end === "start"
                      ? wallDrag.px
                      : (viewport.convertToViewportPoint(w.start.x, w.start.y) as [number, number]);
                  const bPx =
                    wallDrag && wallDrag.wallId === w.id && wallDrag.end === "end"
                      ? wallDrag.px
                      : (viewport.convertToViewportPoint(w.end.x, w.end.y) as [number, number]);
                  return (
                    <g key={`wall-${w.id}`}>
                      <line
                        x1={aPx[0]}
                        y1={aPx[1]}
                        x2={bPx[0]}
                        y2={bPx[1]}
                        style={{
                          stroke: "var(--warn, orange)",
                          strokeWidth: 3 * u,
                          strokeLinecap: "round",
                          cursor: props.mode === "wall" ? "pointer" : "default",
                        }}
                        onClick={(e) => {
                          if (
                            props.mode === "wall" &&
                            (e.altKey || e.ctrlKey || e.metaKey)
                          ) {
                            e.stopPropagation();
                            props.onCommitWalls(
                              props.selectedRoomWalls.filter((x) => x.id !== w.id)
                            );
                          }
                        }}
                      />
                      {props.mode === "wall" &&
                        (["start", "end"] as const).map((end) => {
                          const px = end === "start" ? aPx : bPx;
                          return (
                            <circle
                              key={end}
                              cx={px[0]}
                              cy={px[1]}
                              r={5 * u}
                              style={{
                                fill: "var(--warn, orange)",
                                stroke: "white",
                                strokeWidth: 1.5 * u,
                                cursor: "grab",
                              }}
                              onPointerDown={(e) => onWallEndpointDown(e, w.id, end, px)}
                            />
                          );
                        })}
                    </g>
                  );
                })}
              {props.mode === "wall" && wallStart && (
                <circle cx={wallStart[0]} cy={wallStart[1]} r={5 * u} style={{ fill: "var(--warn, orange)" }} />
              )}

              {/* P2b: neu gezeichnetes Raum-Polygon (erster Punkt groß → anklicken schließt) */}
              {props.mode === "draw" && drawPts.length > 0 && (
                <g>
                  <polyline
                    points={drawPts.map((p) => `${p[0]},${p[1]}`).join(" ")}
                    style={{
                      fill: "none",
                      stroke: "var(--acc)",
                      strokeWidth: 2 * u,
                      strokeDasharray: `${4 * u} ${3 * u}`,
                    }}
                  />
                  {drawPts.map((p, i) => (
                    <circle
                      key={i}
                      cx={p[0]}
                      cy={p[1]}
                      r={(i === 0 ? 6 : 4) * u}
                      style={{
                        fill: i === 0 ? "var(--acc)" : "white",
                        stroke: "var(--acc)",
                        strokeWidth: 1.5 * u,
                      }}
                    />
                  ))}
                </g>
              )}
            </svg>
          )}
        </div>

        {/* Lupe */}
        {magnifier && !loading && (
          <canvas
            ref={magCanvasRef}
            width={MAG_SIZE}
            height={MAG_SIZE}
            style={{
              position: "absolute",
              right: 12,
              bottom: 12,
              width: MAG_SIZE,
              height: MAG_SIZE,
              border: "1px solid var(--acc)",
              borderRadius: 6,
              boxShadow: "0 8px 30px -8px oklch(0 0 0 / 0.6)",
              pointerEvents: "none",
              background: "white",
            }}
          />
        )}
      </div>
    </div>
  );
}

function newWallId(): string {
  return `w-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Ziel-Backing-Dichte aus dem aktuellen Zoom — integer-gestuft (≈ceil(view.scale), damit
 * Schärfe = density·OVERSAMPLE/view.scale ≥ OVERSAMPLE bleibt), mit Hysterese gegen
 * Oszillations-Flackern an Stufengrenzen: hoch sofort bei Bedarf, runter erst, wenn der Zoom
 * deutlich (>1,2) unter die aktuelle Stufe fällt. Auf [1, cap] geklemmt.
 */
function stairDensity(viewScale: number, current: number, cap: number): number {
  let d = current;
  if (viewScale > d) d = Math.ceil(viewScale); // mehr Dichte nötig → sofort hoch
  else if (viewScale < d - 1.2) d = Math.ceil(viewScale); // deutlich darunter → freigeben
  return Math.max(1, Math.min(cap, d));
}

function dropClosingDup(ring: number[][]): number[][] {
  if (ring.length >= 2) {
    const a = ring[0]!;
    const b = ring[ring.length - 1]!;
    if (
      a.length >= 2 &&
      b.length >= 2 &&
      Math.abs(a[0]! - b[0]!) < 1e-9 &&
      Math.abs(a[1]! - b[1]!) < 1e-9
    ) {
      return ring.slice(0, -1);
    }
  }
  return ring;
}
